(() => {
  if (window.WolfPlpPromotions) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const tablet = window.matchMedia('(min-width: 750px)');
  const boundedColumns = (value, maximum, fallback) => Math.max(1, Math.min(maximum, Number.parseInt(value, 10) || fallback));

  function decorateGrid(grid, { page = 1, columnsDesktop = 3, columnsMobile = 1, filtered = grid?.dataset.wolfPromoFiltered === 'true' } = {}) {
    if (!grid) return;
    const template = grid.closest('[data-wolf-collection]')?.querySelector('[data-wolf-plp-promo-template]');
    const existing = [...grid.children].filter((item) => item.hasAttribute('data-wolf-plp-promo'));
    const products = [...grid.children].filter((item) => !item.hasAttribute('data-wolf-plp-promo'));
    const columns = boundedColumns(columnsDesktop, 5, 3);
    const mobileColumns = boundedColumns(columnsMobile, 2, 1);
    grid.dataset.wolfPromoPage = String(page);
    grid.dataset.wolfPromoColumns = String(columns);
    grid.dataset.wolfPromoColumnsMobile = String(mobileColumns);
    grid.dataset.wolfPromoFiltered = String(filtered);

    if (filtered || !template || Number(page) !== 1 || !products.length) {
      existing.forEach((item) => item.remove());
      grid.classList.remove('wolf-plp-grid', 'wolf-plp-grid--horizontal', 'wolf-plp-grid--vertical');
      return;
    }

    const vertical = template.dataset.layout === 'vertical' && columns > 1 && products.length >= 2 * (columns - 1);
    const promo = existing.shift() || template.content.querySelector('[data-wolf-plp-promo]')?.cloneNode(true);
    existing.forEach((item) => item.remove());
    if (!promo) return;
    grid.classList.add('wolf-plp-grid');
    grid.classList.toggle('wolf-plp-grid--vertical', vertical);
    grid.classList.toggle('wolf-plp-grid--horizontal', !vertical);
    grid.style.setProperty('--wolf-plp-columns', columns);
    grid.style.setProperty('--wolf-plp-columns-mobile', mobileColumns);
    grid.style.setProperty('--wolf-plp-mobile-row', Math.ceil(Math.min(2, products.length) / mobileColumns) + 1);
    // Keep keyboard/reading order aligned with each responsive placement.
    const after = Math.min(products.length, desktop.matches ? (vertical ? columns - 1 : columns) : 2);
    if (products[after - 1].nextElementSibling !== promo) products[after - 1].after(promo);
    promo.querySelector('wolf-plp-promotion')?.sync?.();
  }

  function decorateNativeGrids(root = document) {
    root.querySelectorAll('[data-wolf-promo-grid]').forEach((grid) => decorateGrid(grid, {
      page: grid.dataset.wolfPromoPage,
      columnsDesktop: grid.dataset.wolfPromoColumns,
      columnsMobile: grid.dataset.wolfPromoColumnsMobile,
    }));
  }

  class WolfPlpPromotion extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.onMediaChange = () => this.sync();
      desktop.addEventListener('change', this.onMediaChange, options);
      document.addEventListener('visibilitychange', this.onMediaChange, options);
      this.inView = !('IntersectionObserver' in window);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.sync();
        }, { threshold: 0.05 });
        this.observer.observe(this);
      }
      this.sync();
      // Native facets replace only the grid container; reconnecting the promo
      // also restores the reading order for the current viewport.
      queueMicrotask(() => {
        const grid = this.closest('[data-wolf-promo-grid]');
        if (grid) decorateGrid(grid, {
          page: grid.dataset.wolfPromoPage,
          columnsDesktop: grid.dataset.wolfPromoColumns,
          columnsMobile: grid.dataset.wolfPromoColumnsMobile,
        });
      });
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      this.captionObserver?.disconnect();
      this.captionTarget = null;
      this.querySelectorAll('video').forEach((video) => video.pause());
      this.pendingVideo = null;
    }

    shouldPlay() {
      return this.isConnected && this.inView && !document.hidden;
    }

    sync() {
      if (!this.isConnected) return;
      this.syncCaption();
      const vertical = this.closest('.wolf-plp-grid--vertical');
      const kind = desktop.matches ? (vertical ? 'portrait' : 'landscape') : 'mobile';
      const media = this.querySelector(`[data-wolf-promo-media="${kind}"]`);
      const videoTemplate = media?.querySelector('[data-wolf-promo-video]');
      this.querySelectorAll('[data-wolf-promo-media]').forEach((other) => {
        if (other !== media) {
          other.querySelector('video')?.pause();
          other.classList.remove('is-playing');
        }
      });
      this.activeMedia = media;
      this.activeVideo = media?.querySelector('video');
      if (!videoTemplate) return;

      if (!this.shouldPlay()) {
        this.activeVideo?.pause();
        media.classList.remove('is-playing');
        return;
      }
      if (!this.activeVideo) {
        media.append(videoTemplate.content.cloneNode(true));
        this.activeVideo = media.querySelector('video');
        this.activeVideo.muted = true;
        this.activeVideo.defaultMuted = true;
        this.activeVideo.autoplay = true;
        this.activeVideo.loop = true;
        this.activeVideo.playsInline = true;
        this.activeVideo.controls = false;
        this.activeVideo.setAttribute('aria-hidden', 'true');
        this.activeVideo.setAttribute('tabindex', '-1');
      }
      const video = this.activeVideo;
      if (!video.paused) {
        media.classList.add('is-playing');
        return;
      }
      if (this.pendingVideo === video) return;
      this.pendingVideo = video;
      Promise.resolve(video.play()).then(() => {
        if (this.activeVideo !== video || !this.shouldPlay()) {
          video.pause();
          return;
        }
        media.classList.add('is-playing');
      }).catch(() => {
        media.classList.remove('is-playing');
      }).finally(() => {
        if (this.pendingVideo === video) this.pendingVideo = null;
        if (this.activeVideo !== video && this.isConnected) this.sync();
      });
    }

    syncCaption() {
      const grid = this.closest('.wolf-plp-grid--vertical');
      const columns = boundedColumns(grid?.dataset.wolfPromoColumns, 5, 3);
      const products = grid ? [...grid.children].filter((item) => !item.hasAttribute('data-wolf-plp-promo')) : [];
      const neighbor = products[2 * (columns - 1) - 1];
      const caption = neighbor?.querySelector('.card > .card__content, .wolf-color-card__details');
      const promo = this.closest('[data-wolf-plp-promo]');
      if (caption !== this.captionTarget) {
        this.captionObserver?.disconnect();
        this.captionTarget = caption;
        if (caption && 'ResizeObserver' in window) {
          this.captionObserver = new ResizeObserver(() => this.alignCaption());
          this.captionObserver.observe(caption);
        }
      }
      if (!caption) promo?.style.removeProperty('--wolf-plp-caption-height');
      else this.alignCaption();
    }

    alignCaption() {
      if (!this.captionTarget || !this.isConnected) return;
      // End the tall media beside the neighboring product image, leaving its
      // caption below. Observe the caption itself to avoid grid-height feedback.
      this.closest('[data-wolf-plp-promo]')?.style.setProperty(
        '--wolf-plp-caption-height', `${Math.ceil(this.captionTarget.getBoundingClientRect().height)}px`
      );
    }

  }

  if (!customElements.get('wolf-plp-promotion')) customElements.define('wolf-plp-promotion', WolfPlpPromotion);
  window.WolfPlpPromotions = { decorateGrid };
  const start = () => {
    decorateNativeGrids();
    window.dispatchEvent(new CustomEvent('wolf:plp-promotions:ready'));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', (event) => decorateNativeGrids(event.target));
  const resize = () => {
    document.querySelectorAll('.wolf-plp-grid').forEach((grid) => decorateGrid(grid, {
      page: grid.dataset.wolfPromoPage,
      columnsDesktop: grid.dataset.wolfPromoColumns,
      columnsMobile: grid.dataset.wolfPromoColumnsMobile,
    }));
  };
  desktop.addEventListener('change', resize);
  tablet.addEventListener('change', resize);
})();
